import React, { useState, useCallback } from 'react';
import { Check, Loader, X } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Z_INDEX_CLASSES } from '@/constants/zIndex';

import { RootState } from '../../../reducers';
import logger from '../../../lib/logger';
import { requestStatusType } from '../../../types';
import { setupPayments } from '../../../services/subscriptionServices';
import { planPricesPerMonth } from '../../../constants/prices';
import { config } from '../../../../config';
import { createOrUpdateSubscription } from '../thunk';
import { setAlertMessage } from '../../dashboard/dashboardSlice';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { useBooleanFeature } from '../../../hooks/useFeatures';
import { trackStartTrial } from '../../../lib/tracking';

const { stripePublishableKey } = config;
const stripePromise = loadStripe(stripePublishableKey, {
  betas: ['custom_checkout_beta_2'],
});

// Helper function to get relative dates
const getRelativeDate = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const PaymentSection = ({
  onClose,
  trialDays,
}: {
  onClose: () => void;
  trialDays: number;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [upgradeStatus, setUpgradeStatus] = useState<requestStatusType>('idle');

  const { team, settings } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0].teamId;
  const { email } = settings.user;

  const yearlyPrice = planPricesPerMonth.GROWTH.annual * 12; // Define yearlyPrice here

  const handleError = (error: any) => {
    logger.error(error);
    setUpgradeStatus('failed');
  };

  const handleStartTrial = async () => {
    if (!stripe || !elements) {
      logger.error(t('upgrade.freeTrial.errors.stripeError'));
      return;
    }

    setUpgradeStatus('pending');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    try {
      // Collect and verify the payment method
      const { clientSecret } = await setupPayments({
        // userId is implicitly passed with the jwt token
        teamId,
        metadata: {
          toltReferral: window.tolt_referral,
        },
      });

      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${config.appBaseUrl}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        throw error;
      }

      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: 'GROWTH',
          term: 'annual',
          totalAmount: yearlyPrice,
          // Backend checks this and assigns the number of trial days
          trialLabel: `${trialDays} days free growth annual trial`,
          metadata: {
            toltReferral: window.tolt_referral,
          },
        }),
      );

      if (createOrUpdateSubscription.rejected.match(result as never)) {
        onClose();
        const { message = '' } = (result as any).error || {};
        throw new Error(message);
      }

      // Track trial start event
      trackStartTrial({
        plan: 'GROWTH',
        term: 'annual',
        trialLabel: '3 days free growth annual trial',
      });

      // Mark trial modal as shown to prevent TrialDialogManager from re-opening it
      localStorage.setItem('trialModalShown', 'true');

      setUpgradeStatus('succeeded');

      // Important: onClose() has a 250ms delay before calling popModal()
      // We need to wait for that to complete before pushing the success modal
      // Otherwise pushModal happens first, then popModal removes it!
      onClose();
      setTimeout(() => {
        dispatch(
          pushModal({
            type: 'upgradeSuccess',
          }),
        );
      }, 300); // Wait for onClose animation (250ms) + buffer
    } catch (error) {
      setUpgradeStatus('failed');
      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('upgrade.freeTrial.errors.upgradeError', {
            message: error.message,
          }),
        }),
      );
      handleError(error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <PaymentElement
          id="stripe-payment-element"
          options={{
            defaultValues: {
              billingDetails: {
                email,
              },
            },
          }}
        />
      </div>

      <div className="flex items-center justify-center gap-3 py-1">
        <Check className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
        <span className="text-base font-semibold">No payment today</span>
      </div>

      <Button
        type="button"
        variant="default"
        className="w-full h-12 text-base font-bold"
        onClick={handleStartTrial}
        disabled={upgradeStatus === 'pending'}
      >
        {upgradeStatus === 'pending' ? (
          <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            {t('upgrade.freeTrial.processing')}
          </>
        ) : (
          t('upgrade.freeTrial.getFreeTrial')
        )}
      </Button>

      <div className="text-center text-sm text-muted-foreground opacity-60">
        Then ${yearlyPrice}/year (${planPricesPerMonth.GROWTH.annual}/mo)
        starting {getRelativeDate(trialDays)}
      </div>
    </div>
  );
};

interface FreeTrialProps {
  onClose: () => void;
}

const FreeTrial: React.FC<FreeTrialProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { getFeature } = useBooleanFeature();
  const [isClosing, setIsClosing] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  // A/B test: 25% of users get 7-day trial, 75% get 3-day trial
  const is7DayTrial = getFeature('feature_7_day_trial');
  const trialDays = is7DayTrial ? 7 : 3;

  // Handle close with delay to allow Stripe Elements to clean up properly
  // This prevents "removeChild" DOM errors when the dialog unmounts
  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    // First unmount Stripe Elements by hiding content, then close dialog after delay
    setTimeout(() => {
      setIsOpen(false);
      // Additional delay before calling parent onClose to let dialog animation finish
      setTimeout(() => {
        onClose();
      }, 150);
    }, 100);
  }, [isClosing, onClose]);

  const getConversionMessages = (): string[] => {
    return [
      t('upgrade.freeTrial.checks.unlockFeatures', { days: trialDays }),
      t('upgrade.freeTrial.checks.cancelAnytime'),
    ];
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={() => handleClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            `fixed inset-0 ${Z_INDEX_CLASSES.dialogOverlay} bg-white/40 dark:bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            `fixed left-[50%] top-[50%] ${Z_INDEX_CLASSES.dialog} grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white dark:bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg`,
            'max-h-[90vh] overflow-y-auto',
          )}
          data-testid="free-trial-modal"
          onInteractOutside={(e) => {
            // Prevent default close behavior, handle manually
            e.preventDefault();
            handleClose();
          }}
          onEscapeKeyDown={(e) => {
            // Prevent default close behavior, handle manually
            e.preventDefault();
            handleClose();
          }}
        >
          {/* Header Section */}
          <div className="flex flex-col space-y-1.5 text-center sm:text-left pb-6">
            <DialogPrimitive.Title className="text-2xl font-bold">
              {t('upgrade.freeTrial.title', { days: trialDays })}
            </DialogPrimitive.Title>
          </div>

          {/* Content Section */}
          <div className="space-y-6">
            {/* Conversion Messaging */}
            <div className="space-y-4">
              {getConversionMessages().map((text, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-base leading-relaxed flex-1">
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/* Payment section - unmount Stripe Elements first when closing */}
            {!isClosing && (
              <Elements
                stripe={stripePromise}
                options={{
                  mode: 'setup',
                  currency: 'usd',
                  setupFutureUsage: 'off_session',
                }}
              >
                <PaymentSection onClose={handleClose} trialDays={trialDays} />
              </Elements>
            )}
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default FreeTrial;
